import type { AuthSource, Role } from '@/api/types';

const GENERATIONAL = /^(jr|sr|ii|iii)\.?$/i;
const INITIAL = /^\p{L}\.$/u;

/** Title-cases a name typed in capitals ("TROCHMANN" -> "Trochmann"); mixed case is left as typed. */
function tidyCase(word: string): string {
  return word === word.toUpperCase() && word !== word.toLowerCase() ? word.charAt(0) + word.slice(1).toLowerCase() : word;
}

/**
 * The given and family parts of a display name as Microsoft or a person wrote it: "Luis Trochmann",
 * "Trochmann, Luis", "Luis Trochmann (GFT)", "Trochmann Jr., Luis". Parenthetical and generational
 * suffixes are dropped first. A name that cannot be read with confidence (two commas, an initial
 * such as "J." in the way, nothing left) returns null, and callers fall back to the full name.
 */
function nameParts(display: string): { given: string; family: string } | null {
  const bare = display.replace(/\([^)]*\)/g, ' ').trim();
  if (bare.split(',').length > 2) return null;
  const sides = bare.split(',').map((side) =>
    side
      .split(/\s+/)
      .filter((t) => t && !GENERATIONAL.test(t)),
  );
  const [left = [], right] = sides;
  const given = right ?? left;
  const first = given[0];
  if (!first || INITIAL.test(first)) return null;
  // "Last, First": the family name leads the left side; "First Last": it ends the same side.
  const family = right ? left[0] : left.length > 1 ? left[left.length - 1] : undefined;
  return { given: tidyCase(first), family: family ? tidyCase(family) : '' };
}

/** "LT" for "Luis Trochmann" and for "Trochmann, Luis"; '?' for no name. */
export function initialsOf(name: string): string {
  const full = name.trim();
  if (!full) return '?';
  const parts = nameParts(full);
  if (!parts) return fallbackInitials(full);
  return (parts.given.charAt(0) + parts.family.charAt(0)).toUpperCase();
}

function fallbackInitials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

const ROLE_LABELS: Record<Role, string> = {
  user: 'User',
  access_manager: 'Access manager',
  monitor: 'Monitor',
  asset_manager: 'Asset manager',
  industry_manager: 'Industry manager',
  admin: 'Admin',
};

/** Every role, in the order the catalog lists them. */
export const ROLES = Object.keys(ROLE_LABELS) as Role[];

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role];
}

/** Roles in the catalog's order, whatever order the API or the checkboxes gave them. */
export function inCatalogOrder(roles: Role[]): Role[] {
  return ROLES.filter((role) => roles.includes(role));
}

/** All of a person's roles, or what a lone empty set means. `compact` shortens many to "Asset manager +1". */
export function rolesLabel(roles: Role[], compact = false): string {
  const [first, ...rest] = inCatalogOrder(roles);
  if (first === undefined) return 'No role';
  if (compact && rest.length > 0) return `${roleLabel(first)} +${rest.length}`;
  return [first, ...rest].map(roleLabel).join(', ');
}

export function sourceLabel(source: AuthSource): string {
  return source === 'entra' ? 'Entra ID' : 'Local';
}

// Amounts are always US dollars, written the way the browser's region writes
// numbers ($1,234.56 in the US, US$ 1.234,56 in Brazil): `undefined` is the
// browser's locale.
const usd = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usdPrecise = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const integer = new Intl.NumberFormat(undefined);
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const percent = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Money arrives as a decimal string, in USD. Sub-cent amounts keep four decimals. */
export function formatUsd(value: string | number | null | undefined): string {
  const n = Number(value ?? 0);
  if (n !== 0 && Math.abs(n) < 0.01) return usdPrecise.format(n);
  return usd.format(n);
}

export function formatInt(value: number | null | undefined): string {
  return integer.format(value ?? 0);
}

/** 12_345 -> "12.3K" (in the browser's locale) for compact token counts. */
export function formatCompact(value: number): string {
  return compact.format(value);
}

/** 42.25 -> "42.3%" in the browser's locale. */
export function formatPct(value: number): string {
  return `${percent.format(value)}%`;
}

/** "Oct 2, 2026, 05:19 PM" in local time, or in UTC when `utc`. */
export function formatDateTime(iso: string | null, utc = false): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: utc ? 'UTC' : undefined,
  });
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** Share of `part` in `whole` as a CSS width, a visible sliver for any non-zero part. */
export function share(part: number, whole: number): string {
  if (whole <= 0 || part <= 0) return '0%';
  return `${Math.max((part / whole) * 100, 0.8)}%`;
}

/** A calendar day read in UTC (the day a token works through), e.g. "Dec 1, 2026". */
export function formatUtcDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
